import React, { useState, useEffect } from 'react';
import { Ban, FileCheck, Pencil } from 'lucide-react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input/Input';
import { Select } from '@/components/ui/Select/Select';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { VStack } from '@/components/ui/Stack';

export interface ManualProposalData {
  lowestRoomRate: number | null;
  totalCost: number | null;
  foodBeverageMinimum: number | null;
  currencyCode: string;
  notes: string;
  checkInDate: string | null;
  checkOutDate: string | null;
}

interface ManualProposalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ManualProposalData) => Promise<void>;
  onDecline?: (data: ManualProposalData) => Promise<void>;
  venueName: string;
  isSubmitting?: boolean;
  isDeclining?: boolean;
  mode?: 'received' | 'declined';
  /** If provided, modal will be in edit mode with these initial values */
  initialData?: ManualProposalData | null;
}

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'CAD', label: 'CAD - Canadian Dollar' },
  { value: 'AUD', label: 'AUD - Australian Dollar' },
  { value: 'CHF', label: 'CHF - Swiss Franc' },
  { value: 'MXN', label: 'MXN - Mexican Peso' },
  { value: 'JPY', label: 'JPY - Japanese Yen' },
];

export function ManualProposalModal({
  isOpen,
  onClose,
  onSubmit,
  onDecline,
  venueName,
  isSubmitting = false,
  isDeclining = false,
  mode = 'received',
  initialData,
}: ManualProposalModalProps) {
  const [lowestRoomRate, setLowestRoomRate] = useState<string>('');
  const [totalCost, setTotalCost] = useState<string>('');
  const [foodBeverageMinimum, setFoodBeverageMinimum] = useState<string>('');
  const [currencyCode, setCurrencyCode] = useState<string>('USD');
  const [notes, setNotes] = useState<string>('');
  const [checkInDate, setCheckInDate] = useState<string>('');
  const [checkOutDate, setCheckOutDate] = useState<string>('');

  const isEditMode = !!initialData;
  const isDeclineMode = !isEditMode && mode === 'declined';

  // Populate form when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData) {
      setLowestRoomRate(initialData.lowestRoomRate?.toString() || '');
      setTotalCost(initialData.totalCost?.toString() || '');
      setFoodBeverageMinimum(initialData.foodBeverageMinimum?.toString() || '');
      setCurrencyCode(initialData.currencyCode || 'USD');
      setNotes(initialData.notes || '');
      setCheckInDate(initialData.checkInDate || '');
      setCheckOutDate(initialData.checkOutDate || '');
    } else {
      // Reset form for new entries
      setLowestRoomRate('');
      setTotalCost('');
      setFoodBeverageMinimum('');
      setCurrencyCode('USD');
      setNotes('');
      setCheckInDate('');
      setCheckOutDate('');
    }
  }, [initialData, isOpen]);

  function handleClose() {
    onClose();
  }

  function getFormData(): ManualProposalData {
    return {
      lowestRoomRate: lowestRoomRate ? parseFloat(lowestRoomRate) : null,
      totalCost: totalCost ? parseFloat(totalCost) : null,
      foodBeverageMinimum: foodBeverageMinimum ? parseFloat(foodBeverageMinimum) : null,
      currencyCode,
      notes,
      checkInDate: checkInDate || null,
      checkOutDate: checkOutDate || null,
    };
  }

  async function handleSubmit() {
    await onSubmit(getFormData());
  }

  async function handleDecline() {
    if (!onDecline) return;
    await onDecline(getFormData());
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? 'Edit Manual Proposal' : isDeclineMode ? 'Mark Proposal as Declined' : 'Mark Proposal as Received'}
      description={isEditMode
        ? `Update the manually submitted proposal details for ${venueName}`
        : isDeclineMode
          ? `Record that ${venueName} declined outside of Cvent`
        : `Record a manually submitted proposal for ${venueName}`
      }
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isSubmitting || isDeclining}>
            Cancel
          </Button>
          {!isEditMode && onDecline && !isDeclineMode && (
            <Button
              variant="danger"
              onClick={handleDecline}
              isLoading={isDeclining}
              disabled={isSubmitting}
              leftIcon={<Ban className="w-4 h-4" />}
            >
              Declined
            </Button>
          )}
          {isDeclineMode ? (
            <Button
              variant="danger"
              onClick={handleDecline}
              isLoading={isDeclining}
              disabled={isSubmitting}
              leftIcon={<Ban className="w-4 h-4" />}
            >
              Mark Declined
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              disabled={isDeclining}
              leftIcon={isEditMode ? <Pencil className="w-4 h-4" /> : <FileCheck className="w-4 h-4" />}
            >
              {isEditMode ? 'Save Changes' : 'Mark as Received'}
            </Button>
          )}
        </>
      }
    >
      <VStack gap={4}>
        <p className="text-sm text-muted-foreground">
          {isEditMode
            ? 'Update the proposal details below. All fields are optional.'
            : isDeclineMode
              ? 'Use this when the venue declined outside of Cvent (e.g., by email). Add the reason in Notes if you have one.'
            : 'Use this when the venue has submitted their proposal outside of Cvent (e.g., via email). All fields are optional.'
          }
        </p>

        {!isDeclineMode && (
          <>
            <Select
              label="Currency"
              value={currencyCode}
              onValueChange={setCurrencyCode}
              options={CURRENCY_OPTIONS}
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Check-in Date"
                type="date"
                value={checkInDate}
                onChange={(e) => setCheckInDate(e.target.value)}
              />
              <Input
                label="Check-out Date"
                type="date"
                value={checkOutDate}
                onChange={(e) => setCheckOutDate(e.target.value)}
                min={checkInDate || undefined}
              />
            </div>

            <Input
              label="Lowest Room Rate"
              type="number"
              placeholder="e.g., 199"
              value={lowestRoomRate}
              onChange={(e) => setLowestRoomRate(e.target.value)}
              hint="Per night rate"
            />

            <Input
              label="Total Cost"
              type="number"
              placeholder="e.g., 50000"
              value={totalCost}
              onChange={(e) => setTotalCost(e.target.value)}
              hint="Total estimated cost for the event"
            />

            <Input
              label="F&B Minimum"
              type="number"
              placeholder="e.g., 10000"
              value={foodBeverageMinimum}
              onChange={(e) => setFoodBeverageMinimum(e.target.value)}
              hint="Food & beverage minimum requirement"
            />
          </>
        )}

        <Textarea
          label={isDeclineMode ? 'Decline Reason / Notes' : 'Notes'}
          placeholder={isDeclineMode ? 'Add the decline reason from the hotel email...' : 'Any additional notes about this proposal...'}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={isDeclineMode ? 5 : 3}
        />
      </VStack>
    </Modal>
  );
}
